import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import {
	CreateVanCreditMutation,
	ModifyVanCreditMutation,
	useCreateVanCreditMutation,
	useModifyVanCreditMutation,
	useCalculateFirstPaymentDateLazyQuery,
	Maybe,
	Request
} from '@/graphql/__generated__/van-credits';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import toast from 'react-hot-toast';
import VanCreditForm from './CreateVanCredit/VanCreditCreateForm';
import VanCreditReadOnly from './CreateVanCredit/VanCreditReadOnly';

interface CreateVanCreditProps {
	requestData: Maybe<Request> | undefined;
	refetchData: () => Promise<any>;
}

const CreateVanCredit: React.FC<CreateVanCreditProps> = ({ requestData, refetchData }) => {
	const t = useTranslations('CreateVanCredit');
	const vanCreditsClient = useApolloClient(SCHEMAS.VAN_CREDITS);

	const simulation = requestData?.simulation;
	const simulationConfig = simulation?.configuration;
	const isCreditCreated = !!requestData?.creditId;

	const canEdit = !!requestData?.creditId;
	const isCreateMode = !requestData?.creditId;

	const [isEditingMode, setIsEditingMode] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [isRefetching, setIsRefetching] = useState(false);

	const formatDateToString = (date: Date | null): string => {
		if (!date) return '';
		return date.toISOString().split('T')[0];
	};

	const [calculateFirstPaymentDate, { data: firstPaymentData, loading: loadingFirstPayment }] =
		useCalculateFirstPaymentDateLazyQuery({
			client: vanCreditsClient
		});

	const [createVanCredit, { loading: loadingCreate }] = useCreateVanCreditMutation({
		client: vanCreditsClient,
		onCompleted: async (data: CreateVanCreditMutation) => {
			if (data.createVanCredit?.successful) {
				toast.success(t('CreditCreatedSuccessfully'));
				setIsRefetching(true);
				await refetchData();
				setIsRefetching(false);
			} else {
				const errors = data.createVanCredit?.mutationErrors || [];
				const errorMessages = errors
					.map((error) => {
						if (error?.__typename === 'InvalidInput') {
							return error.message;
						}
						if (error?.__typename === 'ActionFailed' || error?.__typename === 'InvalidAction') {
							return error.message;
						}
						return null;
					})
					.filter(Boolean);

				if (errorMessages.length > 0) {
					toast.error(errorMessages.join(', '));
				} else {
					toast.error(t('ErrorCreatingCredit'));
				}
			}
			setConfirmModalOpen(false);
		},
		onError: () => {
			toast.error(t('ErrorCreatingCredit'));
			setConfirmModalOpen(false);
		}
	});

	const [modifyVanCredit, { loading: loadingModify }] = useModifyVanCreditMutation({
		client: vanCreditsClient,
		onCompleted: async (data: ModifyVanCreditMutation) => {
			if (data.modifyVanCredit?.successful) {
				toast.success(t('CreditModifiedSuccessfully'));
				setIsEditingMode(false);
				setSelectedDate(null);
				setIsRefetching(true);
				await refetchData();
				setIsRefetching(false);
			} else {
				const errors = data.modifyVanCredit?.mutationErrors || [];
				const errorMessages = errors
					.map((error) => {
						if (error?.__typename === 'InvalidInput') {
							return error.message;
						}
						if (error?.__typename === 'ActionFailed' || error?.__typename === 'InvalidAction') {
							return error.message;
						}
						return null;
					})
					.filter(Boolean);

				if (errorMessages.length > 0) {
					toast.error(errorMessages.join(', '));
				} else {
					toast.error(t('ErrorModifyingCredit'));
				}
			}
			setConfirmModalOpen(false);
		},
		onError: () => {
			toast.error(t('ErrorModifyingCredit'));
			setConfirmModalOpen(false);
		}
	});

	useEffect(() => {
		const dateString = formatDateToString(selectedDate);
		if (dateString && simulationConfig?.daysPerPeriod) {
			calculateFirstPaymentDate({
				variables: {
					expectedDisbursementDate: dateString,
					daysPerPeriod: simulationConfig.daysPerPeriod
				}
			});
		}
	}, [selectedDate, simulationConfig?.daysPerPeriod, calculateFirstPaymentDate]);

	const handleDateChange = (date: Date | null) => {
		setSelectedDate(date);
	};

	const handleFormSubmit = (date: Date) => {
		setSelectedDate(date);
		setConfirmModalOpen(true);
	};

	const handleConfirmCreate = async () => {
		const dateString = formatDateToString(selectedDate);
		if (!requestData?.requestId || !dateString) return;

		if (isEditingMode && requestData?.creditId) {
			await modifyVanCredit({
				variables: {
					input: {
						creditId: requestData.creditId,
						requestId: requestData.requestId,
						expectedDisbursementDate: dateString
					}
				}
			});
		} else {
			await createVanCredit({
				variables: {
					input: {
						requestId: requestData.requestId,
						expectedDisbursementDate: dateString
					}
				}
			});
		}
	};

	const handleEdit = () => {
		setIsEditingMode(true);
	};

	const handleCancelEdit = () => {
		setIsEditingMode(false);
		setSelectedDate(null);
	};

	const shouldShowForm = isCreateMode || isEditingMode;
	const shouldShowReadOnly = isCreditCreated && !isEditingMode && !isRefetching;

	if (isRefetching) {
		return null;
	}

	if (shouldShowReadOnly) {
		return (
			<>
				<VanCreditReadOnly requestData={requestData} canEdit={canEdit} onEdit={handleEdit} />
			</>
		);
	}

	if (shouldShowForm) {
		return (
			<>
				<VanCreditForm
					requestData={requestData}
					isEditMode={isEditingMode}
					onSubmit={handleFormSubmit}
					onDateChange={handleDateChange}
					loading={loadingCreate || loadingModify}
					firstPaymentDate={firstPaymentData?.calculateFirstPaymentDate}
					loadingFirstPayment={loadingFirstPayment}
					onCancel={isEditingMode ? handleCancelEdit : undefined}
				/>

				<ConfirmModal
					isOpen={confirmModalOpen}
					onClose={() => setConfirmModalOpen(false)}
					onCancel={() => setConfirmModalOpen(false)}
					onConfirm={handleConfirmCreate}
					title={t(isEditingMode ? 'ConfirmModifyCredit' : 'ConfirmCreateCredit')}
					subtitle={t(isEditingMode ? 'ConfirmModifyCreditMessage' : 'ConfirmCreateCreditMessage')}
					confirmActionText={t(isEditingMode ? 'ModifyCredit' : 'CreateCredit')}
					cancelBtnLoading={loadingCreate || loadingModify}
					confirmBtnLoading={loadingCreate || loadingModify}
				/>
			</>
		);
	}

	return null;
};

export default CreateVanCredit;
